import { ReactComponent as SelectIcon } from "../../../common/assets/icons/selectIcon.svg";
import { useEffect, useRef, useState } from "react";

type SelectBoxProps = {
    status?: string;
    className?: string;
    options?: { value: string; label: string }[];
    onChange?: (value: string) => void;
    onSearchChange?: (searchText: string) => void;
    placeholder?: string;
    value?: string;
    searchable?: boolean;
    filterable?: boolean;
    label?: string;
    full?: boolean;
    disabled?: boolean;
    height?: string;
    length?: number;
    disabledBG?: boolean;
    displayValue?: string;
};

const SelectBox = ({
    status,
    className,
    options = [],
    onChange,
    onSearchChange,
    placeholder,
    value,
    searchable = false,
    filterable = false,
    label,
    full,
    disabled,
    height,
    length,
    disabledBG,
    displayValue,
}: SelectBoxProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [selectedOption, setSelectedOption] = useState<{ value: string; label: string } | undefined>(
        options.find(option => option.value === value),
    );

    const selectBoxRef = useRef<HTMLDivElement>(null);

    const displayLabel = displayValue || selectedOption ? selectedOption?.label : null;

    const filteredOptions = filterable ? options.filter(option => option.label.toLowerCase().includes(searchText.toLowerCase())) : options;

    const handleSelect = (selectedValue: string) => {
        if (disabled) return;
        setSearchText("");
        setIsOpen(false);
        const foundOption = options.find(option => option.value === selectedValue);
        if (foundOption) {
            setSelectedOption(foundOption);
        }
        onChange?.(selectedValue);
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (selectBoxRef.current && !selectBoxRef.current.contains(event.target as Node)) {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        const foundOption = options.find(option => option.value === value);
        if (foundOption?.value !== selectedOption?.value) {
            setSelectedOption(foundOption);
        }
    }, [value, options]);

    useEffect(() => {
        if (value === undefined || value === null || value === "") {
            setSelectedOption(undefined);
        }
    }, [value]);

    useEffect(() => {
        if (searchable && onSearchChange) {
            onSearchChange(searchText);
        }
    }, [searchText]);

    useEffect(() => {
        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

    return (
        <div className={`relative rounded-lg ${className}`} ref={selectBoxRef}>
            <div
                className={`flex items-center justify-between rounded-lg ${disabled ? "cursor-default" : "cursor-pointer"} ${disabledBG && "bg-gray6 border-none"} ${
                    status === "focused"
                        ? "border-primary1 py-1.5 px-2.5"
                        : status === "error"
                          ? "border-error border py-1.5 px-2.5"
                          : status === "none"
                            ? "bg-gray6 py-1.5 px-2.5"
                            : status === "view"
                              ? ""
                              : "border-gray4 border py-1.5 px-2.5"
                } ${isOpen ? "border-primary1" : ""}`}
                onClick={() => !disabled && setIsOpen(!isOpen)}
            >
                <span
                    className={`text-sm whitespace-nowrap ${displayLabel || label ? "text-gray2" : "text-grayicon"}`}
                    dangerouslySetInnerHTML={{
                        __html: (() => {
                            const text =
                                label ||
                                (displayLabel
                                    ? full
                                        ? displayLabel.length > (length || displayLabel.length)
                                            ? displayLabel.slice(0, length) + "..."
                                            : displayLabel
                                        : displayLabel.length > 7
                                          ? displayLabel.slice(0, 7) + "..."
                                          : displayLabel
                                    : placeholder || "");
                            return text.replace(/\n/g, "<br />");
                        })(),
                    }}
                />

                <SelectIcon className={`transform ${isOpen ? "rotate-180" : ""}`} />
            </div>

            {isOpen && (
                <div className="max-h-[500px] overflow-y-auto absolute w-full p-2 text-sm mt-1 bg-white border border-gray4 rounded-lg shadow-lg z-20">
                    {searchable && (
                        <input
                            type="text"
                            className="w-full px-2  py-2 mb-2 border border-gray4 rounded-lg outline-none text-sm"
                            placeholder="검색"
                            value={searchText}
                            onChange={e => setSearchText(e.target.value)}
                            autoFocus
                        />
                    )}

                    <div className={`${height} overflow-y-auto`}>
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map(option => (
                                <div
                                    key={option.value}
                                    className={`px-3 whitespace-nowrap rounded-lg py-2 cursor-pointer ${
                                        selectedOption?.value === option.value
                                            ? "bg-primary4 text-primary2 pointer-events-none"
                                            : "hover:bg-primary3 text-primary2"
                                    }`}
                                    onClick={() => handleSelect(option.value)}
                                    dangerouslySetInnerHTML={{ __html: option.label.replace(/\n/g, "<br />") }}
                                />
                            ))
                        ) : (
                            <p className="text-center text-grayicon py-2">검색 결과가 없습니다.</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SelectBox;
